// JourneyRemarkBanner — the engine of remarkability.
//
// Renders just below SiteHeader when all stops on the active
// journey are read. Gives the reader the exact words to say to the next
// person and a link to send them to. Dismissible once per journey.
//
// Godin: "If you don't give them something to say, they're not going to
// say anything." This component gives them something specific to say.

import { useState } from "react";
import { Link } from "wouter";
import { useJourney } from "../hooks/useJourney";
import { getJourney } from "../data/journeys/index";

export default function JourneyRemarkBanner() {
  const { activeJourneyId, isRead } = useJourney();
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const journey = activeJourneyId ? getJourney(activeJourneyId) : null;
  const allDone = journey
    ? journey.stops.every((s) => isRead(s.id))
    : false;

  if (!journey || !allDone || dismissed) return null;

  function handleCopy() {
    navigator.clipboard.writeText(journey!.remarkabilityPrompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    });
  }

  return (
    <div
      style={{
        borderBottom: "1px solid var(--line)",
        borderLeft: "3px solid var(--accent)",
        background: "var(--bg)",
        padding: "14px 24px 14px 21px",
        display: "flex",
        alignItems: "flex-start",
        gap: 20,
        flexWrap: "wrap",
      }}
    >
      {/* Label */}
      <span
        style={{
          fontFamily: "var(--mono)",
          fontSize: 8,
          letterSpacing: 0.9,
          textTransform: "uppercase",
          color: "var(--accent)",
          whiteSpace: "nowrap",
          paddingTop: 2,
          flexShrink: 0,
        }}
      >
        Worth telling someone
      </span>

      {/* Prompt text */}
      <span
        style={{
          fontFamily: "var(--sans)",
          fontSize: 13,
          color: "var(--ink-2)",
          lineHeight: 1.55,
          flex: 1,
          minWidth: 240,
        }}
      >
        {journey.remarkabilityPrompt}
      </span>

      {/* Actions */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
          paddingTop: 1,
        }}
      >
        <button
          onClick={handleCopy}
          style={{
            fontFamily: "var(--mono)",
            fontSize: 9,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            background: "none",
            border: "1px solid var(--line)",
            padding: "5px 10px",
            cursor: "pointer",
            color: copied ? "var(--accent)" : "var(--ink-3)",
            transition: "color 160ms, border-color 160ms",
            whiteSpace: "nowrap",
          }}
        >
          {copied ? "Copied ✓" : "Copy message"}
        </button>

        <Link
          href={journey.remarkabilityHref}
          style={{
            fontFamily: "var(--mono)",
            fontSize: 9,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: "var(--accent)",
            whiteSpace: "nowrap",
          }}
        >
          Send them here →
        </Link>

        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--ink-3)",
            fontFamily: "var(--mono)",
            fontSize: 11,
            padding: "0 2px",
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
